import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { clearToken } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import AdminUsersCard from "../components/AdminUsersCard";
import SettingsBackupCard from "../components/SettingsBackupCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Loader2, Save, Settings as SettingsIcon, Send, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const TEXT_FIELDS = [
  { group: "ITGlue", items: [
    { key: "itglue_api_key", label: "ITGlue API Key", type: "password", help: "From ITGlue → Account → API keys." },
    { key: "itglue_base_url", label: "ITGlue Base URL", type: "text", help: "https://api.itglue.com (US), https://api.eu.itglue.com (EU), https://api.au.itglue.com (AU)" },
  ]},
  { group: "Email (SMTP)", items: [
    { key: "smtp_host", label: "SMTP host", type: "text", help: "e.g., mail.smtp2go.com" },
    { key: "smtp_port", label: "SMTP port", type: "text", help: "2525, 587, or 465" },
    { key: "smtp_user", label: "SMTP username", type: "text" },
    { key: "smtp_pass", label: "SMTP password", type: "password" },
    { key: "smtp_from_email", label: "From email", type: "text", help: "e.g., onboarding@yourdomain.com" },
    { key: "smtp_from_name", label: "From name", type: "text" },
  ]},
  { group: "Microsoft Teams", items: [
    { key: "teams_webhook_url", label: "Workflow URL", type: "text", help: "In Teams: Channel → ••• → Workflows → \"Post to a channel when a webhook request is received\". After setup, copy the HTTP POST URL here. Leave blank to disable Teams notifications." },
  ]},
  { group: "Reminders & webhooks", items: [
    { key: "reminder_interval_days", label: "Reminder cadence (days)", type: "number", help: "How often automated reminders are sent when auto-reminders are on." },
    { key: "webhook_url", label: "Webhook URL (Slack / Teams / custom)", type: "text", help: "POSTed JSON on submission. Leave blank to disable." },
    { key: "webhook_secret", label: "Webhook signing secret (HMAC)", type: "password", help: "If set, each webhook POST will include X-Sentry-Signature: sha256={hmac of body}." },
  ]},
  { group: "Compliance & audit", items: [
    { key: "compliance_packet_email", label: "Compliance officer email", type: "text", help: "Recipient for the monthly audit-log CSV. Also enable the toggle in Notifications for automatic monthly delivery." },
  ]},
  { group: "Settings drift alerts", items: [
    { key: "security_alert_email", label: "Security alert email", type: "text", help: "Where drift alerts land. If blank, alerts fall back to ADMIN_EMAILS. Also enable the master toggle in Notifications below." },
    { key: "settings_drift_maintenance_window", label: "Maintenance window (UTC)", type: "text", help: "Sensitive changes during this window are considered planned and suppress alerts. Format: 'Mon-Fri 09:00-17:00' or 'Sat,Sun 10:00-14:00'. Leave blank for the strictest posture (alert on every change, always)." },
  ]},
];

const TOGGLES = [
  { key: "auto_sync_itglue", label: "Auto-sync data to ITGlue", help: "When ON, every save and submit fires an incremental ITGlue sync (debounced; runs at most once every couple of minutes per onboarding). When OFF, sync only happens when you click the Sync to ITGlue button." },
  { key: "notify_admin_on_submit", label: "Email admins when a client submits", help: "Sends the PDF kickoff packet to ADMIN_EMAILS when the client finishes." },
  { key: "auto_kickoff_packet_to_client", label: "Email kickoff packet to client on submit", help: "Sends the branded PDF summary to the client automatically." },
  { key: "notify_first_open", label: "Email admins when a client first opens the link", help: "Off by default. Useful only if you want very chatty alerts." },
  { key: "notify_first_save", label: "Email admins when a client starts filling", help: "Off by default. Suppresses noise during testing." },
  { key: "auto_reminders_enabled", label: "Send automated periodic reminders", help: "When on, clients with incomplete onboardings get reminded every N days. Off by default so you only send the per-onboarding reminders you schedule yourself." },
  { key: "teams_notify_help_request", label: "Teams: notify on client help-request", help: "Posts an Adaptive Card to the configured Teams channel whenever a client asks for help on a section." },
  { key: "teams_notify_submit", label: "Teams: notify when an onboarding is submitted", help: "Sends a success card with the company name and a link straight to the onboarding." },
  { key: "teams_notify_stuck", label: "Teams: include weekly stuck-client digest", help: "Mirrors the Tuesday email digest into Teams so the team sees stalled clients in chat too." },
  { key: "teams_notify_sync_error", label: "Teams: alert on ITGlue sync errors", help: "Surfaces sync failures so an admin can investigate before clients notice." },
  { key: "settings_backup_weekly_email", label: "Email weekly settings backup (Mon 09:00 UTC)", help: "Every Monday, emails ADMIN_EMAILS a redacted JSON snapshot of your runtime config (secrets are always masked). Gives you an off-site backup + a compliance-friendly audit trail. Off by default." },
  { key: "settings_change_digest_weekly_email", label: "Email weekly settings change digest (Mon 08:00 UTC)", help: "Human-readable 'what changed' summary of settings-history over the last 7 days — colour-coded by source (edit / import / restore), per-user breakdown, top-touched fields. Lands one hour before the raw backup so admins see the story first. Off by default." },
  { key: "compliance_packet_enabled", label: "Email monthly compliance packet (1st of month 09:00 UTC)", help: "Ships the audit-log CSV (last 30 days) to your compliance officer for SOC2 CC7.2 / HIPAA §164.312(b) evidence. Set the recipient email in the Compliance & audit section above. Off by default." },
  { key: "settings_drift_alerts_enabled", label: "Live drift alerts on sensitive-field changes", help: "Fires an instant email + Teams card whenever an integration key or webhook secret is edited OUTSIDE the configured maintenance window. Set the recipient + window in the 'Settings drift alerts' section above. Off by default." },
];

export default function AdminSettings() {
  const nav = useNavigate();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingTeams, setTestingTeams] = useState(false);
  const [sendingCompliancePacket, setSendingCompliancePacket] = useState(false);
  const [sendingDriftAlert, setSendingDriftAlert] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/settings");
      setData(r.data);
    } catch (e) { if (e?.response?.status === 401) { clearToken(); nav("/login"); } }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      TEXT_FIELDS.forEach(g => g.items.forEach(f => {
        const v = data[f.key];
        if (v === undefined || v === null) return;
        if (typeof v === "string" && v.includes("•")) return;
        payload[f.key] = f.type === "number" ? Number(v) : v;
      }));
      TOGGLES.forEach(t => {
        if (typeof data[t.key] === "boolean") payload[t.key] = data[t.key];
      });
      await api.post("/settings", payload);
      toast.success("Settings saved");
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const testWebhook = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/test-webhook");
      toast.success(`Webhook test sent ${r.data.signed ? "(signed)" : "(unsigned)"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setTesting(false); }
  };

  const testTeams = async () => {
    setTestingTeams(true);
    try {
      const r = await api.post("/settings/test-teams");
      toast.success(`Teams card sent (HTTP ${r.data.status_code})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Teams test failed"); }
    finally { setTestingTeams(false); }
  };

  const sendCompliancePacketNow = async () => {
    setSendingCompliancePacket(true);
    try {
      const r = await api.post("/settings/compliance-packet-now?window_days=30");
      toast.success(`Compliance packet sent to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compliance packet send failed");
    } finally {
      setSendingCompliancePacket(false);
    }
  };

  const sendDriftAlertTest = async () => {
    setSendingDriftAlert(true);
    try {
      const r = await api.post("/settings/drift-alert-test");
      const n = r.data?.sent_to ?? 0;
      toast.success(n ? `Test drift alert emailed to ${n} recipient(s)` : "No recipients configured — no email sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drift alert test failed");
    } finally {
      setSendingDriftAlert(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="animate-spin text-brand-primary"/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <BrandHeader showAuditLog showSecurityEvents showImport showClients showLogout/>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-4">
          <button onClick={() => nav("/admin")} className="text-sm text-slate-600 hover:text-brand-primary inline-flex items-center gap-1" data-testid="back-to-admin-btn">
            <ChevronLeft size={14}/>Back to dashboard
          </button>
        </div>
        <div className="mb-8 flex items-center gap-3">
          <SettingsIcon size={28} className="text-brand-primary"/>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-brand-primary">Settings</h1>
            <p className="text-slate-600 mt-1">Integrations, email, notifications, and cadence.</p>
          </div>
        </div>

        <div className="space-y-6" data-testid="settings-form">
          {/* Backup & migration + Change history (extracted for readability) */}
          <SettingsBackupCard onSettingsChanged={load}/>

          {TEXT_FIELDS.map(g => (
            <Card key={g.group} className="bg-white border-slate-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4 text-brand-primary">{g.group}</h2>
              <div className="space-y-4">
                {g.items.map(f => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={f.key} className="text-slate-800">{f.label}</Label>
                    <Input id={f.key} type={f.type === "password" ? "text" : f.type}
                      value={data[f.key] ?? ""} onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                      placeholder={f.help || ""}
                      data-testid={`settings-${f.key}`}
                    />
                    {f.help && <p className="text-xs text-slate-500">{f.help}</p>}
                  </div>
                ))}
                {g.group === "Reminders & webhooks" && (
                  <Button onClick={testWebhook} disabled={testing || !data.webhook_url} variant="outline" size="sm" className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="test-webhook-btn">
                    {testing ? <Loader2 className="animate-spin" size={14}/> : <><Send size={14} className="mr-1.5"/>Send test webhook</>}
                  </Button>
                )}
                {g.group === "Microsoft Teams" && (
                  <Button onClick={testTeams} disabled={testingTeams || !data.teams_webhook_url_set} variant="outline" size="sm" className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="test-teams-btn" title={data.teams_webhook_url_set ? "" : "Save the workflow URL first, then send a test card."}>
                    {testingTeams ? <Loader2 className="animate-spin" size={14}/> : <><Send size={14} className="mr-1.5"/>Send test card to Teams</>}
                  </Button>
                )}
                {g.group === "Compliance & audit" && (
                  <Button
                    onClick={sendCompliancePacketNow}
                    disabled={sendingCompliancePacket || !data.compliance_packet_email}
                    variant="outline"
                    size="sm"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    data-testid="send-test-compliance-packet-btn"
                    title={data.compliance_packet_email ? "Sends the last 30 days of audit-log CSV to the compliance officer email above." : "Save a compliance officer email first."}
                  >
                    {sendingCompliancePacket ? <Loader2 className="animate-spin" size={14}/> : <><Send size={14} className="mr-1.5"/>Send test compliance packet</>}
                  </Button>
                )}
                {g.group === "Settings drift alerts" && (
                  <Button
                    onClick={sendDriftAlertTest}
                    disabled={sendingDriftAlert}
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="send-test-drift-alert-btn"
                    title="Sends a synthetic drift alert (referencing 3 sample sensitive fields) so you can preview the amber-banner email + Teams card before opting in."
                  >
                    {sendingDriftAlert ? <Loader2 className="animate-spin" size={14}/> : <><Send size={14} className="mr-1.5"/>Send test drift alert</>}
                  </Button>
                )}
              </div>
            </Card>
          ))}

          <AdminUsersCard />

          <Card className="bg-white border-slate-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-1 text-brand-primary">Email notifications</h2>
            <p className="text-xs text-slate-500 mb-4">Granular control over what gets emailed. Defaults are quiet so you don&apos;t spam during testing.</p>
            <div className="space-y-4">
              {TOGGLES.map(t => (
                <label key={t.key} className="flex items-start gap-3 cursor-pointer" data-testid={`toggle-${t.key}-row`}>
                  <Switch
                    checked={!!data[t.key]}
                    onCheckedChange={(checked) => setData({ ...data, [t.key]: checked })}
                    data-testid={`toggle-${t.key}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">{t.label}</div>
                    {t.help && <div className="text-xs text-slate-500 mt-0.5">{t.help}</div>}
                  </div>
                </label>
              ))}
            </div>
          </Card>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="settings-save-btn">
              {saving ? <Loader2 className="animate-spin" size={16}/> : <><Save size={16} className="mr-2"/>Save settings</>}
            </Button>
          </div>
          <p className="text-xs text-slate-500 text-center">Passwords/keys are masked above. Leave masked values unchanged — only edit fields you want to update.</p>
        </div>
      </main>
      <BrandFooter/>
    </div>
  );
}
